import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Eye, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { STATIC_DOCS } from "../content/siteContent";
import { locDocField } from "../content/i18n_extra";
import { api } from "../lib/api";
import { ConsentModal } from "./ConsentDocs";

export function DocumentsSection({ full = false }) {
  const { t, lang } = useLang();
  const [remoteDocs, setRemoteDocs] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);

  useEffect(() => {
    api.get("/documents").then((r) => setRemoteDocs(r.data)).catch(() => {});
  }, []);

  return (
    <section className="bg-white py-28 sm:py-36" data-testid="documents-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <div className="text-xs font-heading font-bold tracking-[0.18em] sm:tracking-[0.3em] uppercase text-brand mb-4">{t("docs_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("docs_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("docs_sub")}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {STATIC_DOCS.map((doc, i) => (
            <motion.div
              key={doc.key}
              data-testid={`static-doc-${doc.key}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-black/5 p-8 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand flex items-center justify-center"><FileText size={19} /></div>
                <span className="text-[10px] font-bold tracking-widest uppercase text-sub">{locDocField(doc.key, "category", lang, doc.category)}</span>
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold text-ink leading-snug">{locDocField(doc.key, "title", lang, doc.title)}</h3>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed flex-1">{locDocField(doc.key, "desc", lang, doc.desc)}</p>
              <div className="mt-6 flex gap-3">
                <button data-testid={`doc-view-${doc.key}`} onClick={() => setOpenDoc(doc.key)} className="flex items-center gap-2 h-10 px-5 rounded-full bg-ink text-white text-xs font-semibold hover:bg-black transition-colors">
                  <Eye size={14} /> {t("docs_view")}
                </button>
                <a href={doc.file} download data-testid={`doc-download-${doc.key}`} className="flex items-center gap-2 h-10 px-5 rounded-full border border-black/15 text-ink text-xs font-semibold hover:bg-black/5 transition-colors">
                  <Download size={14} /> {t("docs_download")}
                </a>
              </div>
            </motion.div>
          ))}

          {remoteDocs.map((doc, i) => (
            <motion.div
              key={doc.id}
              data-testid={`remote-doc-${doc.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-black/5 p-8 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><FileText size={19} /></div>
                <span className="text-[10px] font-bold tracking-widest uppercase text-sub">{doc.category}</span>
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold text-ink leading-snug flex-1">{doc.title}</h3>
              <div className="mt-6">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`} data-testid={`doc-download-${doc.id}`} className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-ink text-white text-xs font-semibold hover:bg-black transition-colors">
                  <Download size={14} /> {t("docs_download")}
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {!full && (
          <div className="mt-10">
            <Link to="/belgeler" data-testid="docs-see-all" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-[gap] duration-300">
              {t("nav_documents")} <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>
      {openDoc && <ConsentModal docKey={openDoc} onClose={() => setOpenDoc(null)} />}
    </section>
  );
}
